import { Lock, Users } from 'lucide-react';
import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  REMARK_BODY_MAX,
  REMARK_TAGS,
  REMARK_TAG_LABELS,
  REMARK_VISIBILITY_LABELS,
  type MentionablePerson,
  type RemarkTag,
  type RemarkVisibility,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Textarea } from '../../../components/Input';
import { Kbd } from '../../../components/Kbd';
import { Segmented } from '../../../components/Segmented';
import { useFocusOnOpen } from '../../../lib/use-focus-on-open';
import type { RemarkDraft } from '../api/remarks-api';
import { RemarkDot } from './RemarkTags';
import styles from './remarks.module.css';

// F23.13 (ADR 0049): one box for a remark — tags in one click each, text, @mentions on a Team remark, Team
// or Only me. Enter saves, Shift+Enter starts a new line, Esc closes (the pop-over's own key).

const VISIBILITY_OPTIONS = [
  { value: 'team' as const, label: REMARK_VISIBILITY_LABELS.team },
  { value: 'private' as const, label: REMARK_VISIBILITY_LABELS.private },
];

/** "@Far" just before the caret: what the mention list filters on. */
const MENTION_QUERY = /(^|\s)@([\p{L}.'-]*)$/u;
const MENTION_SUGGESTIONS_MAX = 6;

interface RemarkComposerProps {
  readonly heading: string;
  readonly initial?: RemarkDraft;
  /** Who this job lets a Team remark mention; empty switches mentions off (one remark on many people). */
  readonly mentionable: readonly MentionablePerson[];
  readonly submitLabel?: string;
  readonly onSubmit: (draft: RemarkDraft) => void;
  readonly onCancel: () => void;
  /** Focus the text on open: a pop-over's composer is opened to be typed in. */
  readonly autoFocus?: boolean;
}

const EMPTY_DRAFT: RemarkDraft = { tags: [], body: '', visibility: 'team', mentionUserIds: [] };

export function RemarkComposer({ heading, initial = EMPTY_DRAFT, mentionable, submitLabel = 'Save', onSubmit, onCancel, autoFocus = false }: RemarkComposerProps): ReactNode {
  const textId = useId();
  const listId = useId();
  const textRef = useRef<HTMLTextAreaElement | null>(null);
  const [tags, setTags] = useState<readonly RemarkTag[]>(initial.tags);
  const [body, setBody] = useState(initial.body);
  const [visibility, setVisibility] = useState<RemarkVisibility>(initial.visibility);
  const [mentioned, setMentioned] = useState<readonly MentionablePerson[]>(() => mentionable.filter((person) => initial.mentionUserIds.includes(person.userId)));
  const [query, setQuery] = useState<string | null>(null);
  const [highlight, setHighlight] = useState(0);
  useFocusOnOpen(textRef, autoFocus);

  const canMention = visibility === 'team' && mentionable.length > 0;
  const suggestions = useMemo(() => {
    if (!canMention || query === null) return [];
    const needle = query.toLowerCase();
    return mentionable.filter((person) => person.name.toLowerCase().split(/\s+/).some((part) => part.startsWith(needle)) || person.name.toLowerCase().startsWith(needle)).slice(0, MENTION_SUGGESTIONS_MAX);
  }, [canMention, query, mentionable]);
  const hasContent = tags.length > 0 || body.trim() !== '';

  function toggle(tag: RemarkTag): void {
    setTags((current) => (current.includes(tag) ? current.filter((item) => item !== tag) : REMARK_TAGS.filter((item) => item === tag || current.includes(item))));
  }

  function onText(value: string, caret: number): void {
    setBody(value);
    const match = MENTION_QUERY.exec(value.slice(0, caret));
    setQuery(match ? (match[2] ?? '') : null);
    setHighlight(0);
  }

  function pick(person: MentionablePerson): void {
    const element = textRef.current;
    const caret = element?.selectionStart ?? body.length;
    const before = body.slice(0, caret).replace(MENTION_QUERY, (_whole, space: string) => `${space}@${person.name} `);
    const next = `${before}${body.slice(caret)}`;
    setBody(next);
    setMentioned((current) => (current.some((item) => item.userId === person.userId) ? current : [...current, person]));
    setQuery(null);
    requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(before.length, before.length);
    });
  }

  function submit(): void {
    if (!hasContent) return;
    // A mention counts while its name is still in the text: deleting "@Nabil Bari" un-mentions him.
    const kept = canMention ? mentioned.filter((person) => body.includes(`@${person.name}`)) : [];
    onSubmit({ tags, body: body.trim(), visibility, mentionUserIds: kept.map((person) => person.userId) });
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (suggestions.length > 0) setQuery(null);
      else onCancel();
      return;
    }
    if (suggestions.length > 0) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlight((current) => (current + (event.key === 'ArrowDown' ? 1 : -1) + suggestions.length) % suggestions.length);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        const person = suggestions[highlight];
        if (person) pick(person);
        return;
      }
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  }

  return (
    <div className={styles.composer}>
      <div className={styles.composerHead}>
        <span className={styles.composerTitle}>{heading}</span>
        <span className={styles.composerNote}>
          {visibility === 'team' ? 'HR and the job’s managers can see it.' : 'Only you can see it.'} A remark never changes the score.
        </span>
      </div>
      <div className={styles.tagPicker} role="group" aria-label="Tags">
        {REMARK_TAGS.map((tag) => (
          <button key={tag} type="button" className={styles.tagToggle} aria-pressed={tags.includes(tag)} onClick={() => toggle(tag)}>
            <RemarkDot tags={[tag]} />
            {REMARK_TAG_LABELS[tag]}
          </button>
        ))}
      </div>
      <div className={styles.textWrap}>
        <label htmlFor={textId} className={styles.srOnly}>
          Remark text
        </label>
        <Textarea
          id={textId}
          ref={textRef}
          rows={2}
          value={body}
          maxLength={REMARK_BODY_MAX}
          placeholder={canMention ? 'Write a remark — type @ to mention someone' : 'Write a remark'}
          aria-autocomplete={canMention ? 'list' : undefined}
          aria-controls={suggestions.length > 0 ? listId : undefined}
          onChange={(event) => onText(event.target.value, event.target.selectionStart)}
          onKeyDown={onKeyDown}
        />
        {suggestions.length > 0 && (
          <ul id={listId} className={styles.mentionList} role="listbox" aria-label="People you can mention">
            {suggestions.map((person, index) => (
              <li key={person.userId} role="option" aria-selected={index === highlight}>
                <button type="button" className={styles.mentionOption} onMouseDown={(event) => event.preventDefault()} onClick={() => pick(person)}>
                  {person.name}
                  <span className={styles.mentionRole}>{person.role === 'hr' ? 'HR' : 'Manager — sees it once on their shortlist'}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className={styles.composerFoot}>
        <Segmented label="Who can see this remark" value={visibility} options={VISIBILITY_OPTIONS} onChange={setVisibility} />
        <span className={styles.visibilityHint} aria-hidden>
          {visibility === 'team' ? <Users size={14} /> : <Lock size={14} />}
        </span>
        <span className={styles.footSpacer} />
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" onClick={submit} disabled={!hasContent}>
          {submitLabel} <Kbd inverse>Enter</Kbd>
        </Button>
      </div>
    </div>
  );
}
